import type { Pt } from './types.js';

/** Affine transform `x' = a·x + c·y + e`, `y' = b·x + d·y + f` (SVG matrix order). */
export interface Affine {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const IDENTITY: Affine = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

const RAD = Math.PI / 180;

export function applyAffine(m: Affine, p: Pt): Pt {
  return { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
}

/** `compose(m, n)` applies `n` first, then `m`. */
export function compose(m: Affine, n: Affine): Affine {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e,
    f: m.b * n.e + m.d * n.f + m.f,
  };
}

export function invert(m: Affine): Affine {
  const det = m.a * m.d - m.b * m.c;
  if (det === 0) throw new Error('Affine transform is not invertible');
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

export function translation(dx: number, dy: number): Affine {
  return { ...IDENTITY, e: dx, f: dy };
}

/** Rotation by `deg` degrees (clockwise on screen, y down) around `about`. */
export function rotation(deg: number, about: Pt = { x: 0, y: 0 }): Affine {
  const cos = Math.cos(deg * RAD);
  const sin = Math.sin(deg * RAD);
  return compose(
    translation(about.x, about.y),
    compose({ a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 }, translation(-about.x, -about.y)),
  );
}

export function scaling(sx: number, sy: number, about: Pt = { x: 0, y: 0 }): Affine {
  return compose(
    translation(about.x, about.y),
    compose({ a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 }, translation(-about.x, -about.y)),
  );
}

/** Uniform scale factor of a transform (√|det|), used to scale stroke widths. */
export function scaleFactor(m: Affine): number {
  return Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
}

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Distance from `p` to the segment `a`–`b`. */
export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Distance from `p` to a polyline (a single point counts as a degenerate segment). */
export function distToPolyline(p: Pt, line: readonly Pt[]): number {
  if (line.length === 1) return dist(p, line[0]!);
  let best = Infinity;
  for (let i = 1; i < line.length; i++)
    best = Math.min(best, distToSegment(p, line[i - 1]!, line[i]!));
  return best;
}

/** Even-odd point-in-polygon test (polygon implicitly closed). */
export function pointInPolygon(p: Pt, poly: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)
      inside = !inside;
  }
  return inside;
}

/** Rotated rectangle given by its unrotated top-left corner, size and rotation (deg) around the centre. */
export interface RotatedRect {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

export function rectCenter(r: RotatedRect): Pt {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

export function pointInRotatedRect(p: Pt, r: RotatedRect): boolean {
  const c = rectCenter(r);
  const local = applyAffine(rotation(-r.rotation, c), p);
  return Math.abs(local.x - c.x) <= r.w / 2 && Math.abs(local.y - c.y) <= r.h / 2;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Bounding box of points (null for none). */
export function boundsOf(points: readonly Pt[]): Bounds | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY };
}

export function boundsIntersect(a: Bounds, b: Bounds, margin = 0): boolean {
  return (
    a.minX - margin <= b.maxX &&
    b.minX - margin <= a.maxX &&
    a.minY - margin <= b.maxY &&
    b.minY - margin <= a.maxY
  );
}
